import { format, parseISO } from 'date-fns';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { Icon, IconArrowDown, IconArrowLeft, IconArrowRight } from '@flixbus/honeycomb-icons-react';
import { Pager, PagerItem, Tag } from '@flixbus/honeycomb-react';

import Loader from 'common/Loader';
import {
	ACTION_TYPE_LABELS,
	TYPE_TO_CATEGORY,
} from 'pages/CrewMemberDetails/components/CrewMemberActionLog/const';
import { typeTags } from 'pages/CrewMemberDetails/components/CrewMemberActionLog/groupedFilterTypes';
import {
	getRouteFromArray,
	renderParamRow,
	renderWaSummary,
} from 'pages/CrewMemberDetails/components/CrewMemberActionLog/WaLogDetails';

import { actionLogStore } from './ActionLogStore';
import { rideDetailsActionLog } from './text';

import styles from './styles.module.css';

const timezoneOffset = format(new Date(), 'xxx');

const ActionLogRow = ({ action }) => {
	const [expanded, setExpanded] = useState(false);
	const { crewMemberId, crewMemberName, type, parameters, createdAt, createdBy } = action;
	const categoryKey = TYPE_TO_CATEGORY[type];
	const firstParam = parameters?.[0];
	const route = getRouteFromArray(parameters);

	return (
		<div className={styles.rowCard}>
			<div className={styles.rowGrid}>
				<div>
					<strong>{ACTION_TYPE_LABELS[type] ?? type}</strong>
				</div>
				<div>
					<div className={styles.detailsLine}>
						<Link to={`/crew-management/${crewMemberId}`} className={styles.driverName}>
							{crewMemberName}
						</Link>
						{route && <span> - {route}</span>}
						{parameters?.length > 0 && (
							<span className={styles.versionBadge}>{parameters.length}</span>
						)}
						<button
							type="button"
							className={styles.expandToggle}
							onClick={() => setExpanded((prev) => !prev)}
							aria-expanded={expanded}
						>
							<Icon InlineIcon={expanded ? IconArrowDown : IconArrowRight} size={3} />
						</button>
					</div>
					{firstParam && (
						<div className={styles.summaryLine}>{renderWaSummary(type, firstParam)}</div>
					)}
				</div>
				<div>
					{categoryKey && (
						<Tag display="subtle" appearance="neutral" extraClasses={styles.categoryTag}>
							{typeTags[categoryKey]}
						</Tag>
					)}
				</div>
				<div>{format(parseISO(createdAt), 'd MMM, HH:mm')}</div>
				<div>{createdBy}</div>
				{expanded && parameters?.length > 0 && (
					<div className={styles.expandedParams}>
						{parameters.map((param) => (
							<div key={`${param.stationFrom}-${param.stationTo}`} className={styles.paramRow}>
								{renderParamRow(type, param)}
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

const ActionLog = observer(() => {
	const { actions, isLoading, isError } = actionLogStore;
	const [page, setPage] = React.useState(0);

	const renderData = () => {
		if (actions.length === 0) {
			return <div className={styles.noActions}>{rideDetailsActionLog.noActions}</div>;
		}

		return actions[page].map((action) => <ActionLogRow key={action.id} action={action} />);
	};

	const prevPage = () => {
		if (page === 0) return;
		setPage((currentPage) => currentPage - 1);
	};

	const nextPage = () => {
		if (page === actions.length - 1) return;
		setPage((currentPage) => currentPage + 1);
	};

	const renderPagination = () => {
		if (actions.length <= 1) return null;
		return (
			<div className={styles.pagination}>
				<Pager Elem="button" aria-label={rideDetailsActionLog.paginationLabel}>
					<PagerItem
						Elem="button"
						disabled={page === 0}
						aria-label={rideDetailsActionLog.prevPage}
						onClick={prevPage}
					>
						<Icon InlineIcon={IconArrowLeft} />
					</PagerItem>
					{actions.map((batch, index) => (
						<PagerItem key={batch[0].id} active={page === index} onClick={() => setPage(index)}>
							{index + 1}
						</PagerItem>
					))}
					<PagerItem
						Elem="button"
						disabled={page === actions.length - 1}
						aria-label={rideDetailsActionLog.nextPage}
						onClick={nextPage}
					>
						<Icon InlineIcon={IconArrowRight} />
					</PagerItem>
				</Pager>
			</div>
		);
	};

	return (
		<div className={styles.actionLog}>
			<h3 className={styles.title}>{rideDetailsActionLog.title}</h3>
			<div className={styles.headerRow}>
				<div>{rideDetailsActionLog.headerAction}</div>
				<div>{rideDetailsActionLog.headerDetails}</div>
				<div>{rideDetailsActionLog.headerCategory}</div>
				<div>
					{rideDetailsActionLog.headerDateTime}
					<br />
					(UTC {timezoneOffset})
				</div>
				<div>{rideDetailsActionLog.headerUser}</div>
			</div>
			{!isLoading && !isError && renderData()}
			{isError && <div className={styles.noActions}>{rideDetailsActionLog.errorMessage}</div>}
			{isLoading && <Loader />}
			{!isLoading && !isError && renderPagination()}
		</div>
	);
});

export default ActionLog;
